import dayjs from 'dayjs';
import 'dayjs/locale/es';
import {
    readOrbitCitizenString,
    readOrbitString,
    resolveOrbitPreferredContact,
} from '../../../../../shared/utils/orbitCaseDetail.util';
import {
    OrbitDocumentTypeEnum,
    OrbitPersonTypeEnum,
} from '../../constants/citizenCatalogs';

dayjs.locale('es');

export type OrbitHistoryTableRow = {
    id: string;
    case_number: string;
    citizen_document: string;
    person_type: string;
    citizen_name: string;
    case_type_name: string;
    created_at: string;
    status_name: string;
    description: string;
    resolution: string;
    preferred_contact_method: string;
    citizen_email: string;
    citizen_phone: string;
};

const CREATED_AT_FORMAT = 'D [de] MMMM [de] YYYY [a las] HH:mm';

const sanitizeCell = (value: unknown): string => {
    if (value === null || value === undefined) return '';
    const text = String(value).trim();
    const upper = text.toUpperCase();
    return !text || upper === 'NULL' || upper === 'N' ? '' : text;
};

const formatCreatedAt = (raw: string): string => {
    if (!raw) return '';
    const parsed = dayjs(raw);
    return parsed.isValid() ? parsed.format(CREATED_AT_FORMAT) : raw;
};

const resolvePersonType = (orbitCase: Record<string, unknown>): string => {
    const customFields =
        orbitCase.custom_fields &&
        typeof orbitCase.custom_fields === 'object' &&
        !Array.isArray(orbitCase.custom_fields)
            ? (orbitCase.custom_fields as Record<string, unknown>)
            : undefined;

    const fromCustomFields = readOrbitString(customFields ?? {}, [
        'person_type',
    ]);

    if (fromCustomFields) return fromCustomFields;

    const documentType = readOrbitCitizenString(orbitCase, [
        'document_type',
    ]).toUpperCase();

    if (documentType === OrbitDocumentTypeEnum.NIT) {
        return OrbitPersonTypeEnum.Juridica;
    }

    return documentType ? OrbitPersonTypeEnum.Natural : '';
};

export const mapOrbitCaseToHistoryRow = (
    orbitCase: Record<string, unknown>,
    fallbackDocument?: string
): OrbitHistoryTableRow => ({
    id: readOrbitString(orbitCase, ['id']),
    case_number: sanitizeCell(readOrbitString(orbitCase, ['case_number'])),
    citizen_document:
        sanitizeCell(readOrbitCitizenString(orbitCase, ['document'])) ||
        fallbackDocument ||
        '',
    person_type: resolvePersonType(orbitCase),
    citizen_name: sanitizeCell(
        readOrbitCitizenString(orbitCase, ['full_name'])
    ),
    case_type_name: sanitizeCell(
        readOrbitString(orbitCase, [
            'case_type_label',
            'case_type_name',
            'case_type',
        ])
    ),
    created_at: formatCreatedAt(readOrbitString(orbitCase, ['created_at'])),
    status_name: sanitizeCell(
        readOrbitString(orbitCase, ['status_label', 'status_name', 'status'])
    ),
    description: sanitizeCell(readOrbitString(orbitCase, ['description'])),
    resolution: sanitizeCell(readOrbitString(orbitCase, ['resolution'])),
    preferred_contact_method: resolveOrbitPreferredContact(orbitCase),
    citizen_email: sanitizeCell(readOrbitCitizenString(orbitCase, ['email'])),
    citizen_phone: sanitizeCell(
        readOrbitCitizenString(orbitCase, ['mobile_phone', 'phone'])
    ),
});
